import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../../components/ui/use-toast';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Checkbox } from '../../components/ui/checkbox';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { shopifyService } from '../../services/shopifyService';
import { useAuth } from '../../contexts/AuthContext';
import FeedXmlImportModal, { FeedXmlConfig } from "../../components/feed-xml/FeedXmlImportModal";
import WooCommerceProductImport from "../../components/integrations/WooCommerceProductImport";

interface ImportSource {
  id: string;
  name: string;
  icon: string;
  description: string;
  enabled: boolean;
}

interface ShopifyProduct {
  id: string;
  title: string;
  description: string;
  images: { src: string }[];
  variants: any[];
  companyId: string;
  currency: string;
  alreadyImported?: boolean;
}

const importSources: ImportSource[] = [
  {
    id: 'shopify',
    name: 'Shopify',
    icon: '',
    description: 'Importe produtos diretamente da sua loja Shopify.',
    enabled: true
  },
  {
    id: 'woocommerce',
    name: 'WooCommerce',
    icon: '',
    description: 'Importe produtos do seu site WordPress com WooCommerce.',
    enabled: true
  },
  {
    id: 'feed-xml',
    name: 'Feed XML',
    icon: '',
    description: 'Importe produtos a partir de um feed XML.',
    enabled: true
  },
  {
    id: 'prestashop',
    name: 'PrestaShop',
    icon: '',
    description: 'Importe produtos da sua loja PrestaShop.',
    enabled: false
  },
  {
    id: 'csv',
    name: 'CSV',
    icon: '',
    description: 'Importe produtos a partir de um arquivo CSV.',
    enabled: false
  },
  {
    id: 'manual',
    name: 'Manual',
    icon: '',
    description: 'Adicione produtos manualmente um a um.',
    enabled: true
  }
];

export default function Import() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState<ShopifyProduct[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [showProductsModal, setShowProductsModal] = useState(false);
  const { currentUser } = useAuth();
  const [shopifyConfigured, setShopifyConfigured] = useState(false);
  const [wooCommerceConfigured, setWooCommerceConfigured] = useState(false);
  const [showFeedXmlModal, setShowFeedXmlModal] = useState(false);
  const [showWooCommerceModal, setShowWooCommerceModal] = useState(false);
  const [wooCommerceConfig, setWooCommerceConfig] = useState<any>(null);

  // Verificar se as integrações estão configuradas
  useEffect(() => {
    const checkIntegrationsConfig = async () => {
      if (!currentUser?.companyId) return;
      
      try {
        // Verificar se existe configuração do Shopify e WooCommerce na coleção integrations
        const integrationsRef = collection(db, 'integrations');
        const integrationsQuery = query(integrationsRef, where('companyId', '==', currentUser.companyId));
        const integrationsSnapshot = await getDocs(integrationsQuery);
        
        let hasShopify = false;
        let hasWooCommerce = false;
        let wooCommerceConfigData = null;
        
        integrationsSnapshot.forEach(doc => {
          const data = doc.data();
          if (data.type === 'shopify' && data.active) {
            hasShopify = true;
          }
          if (data.type === 'woocommerce' && data.active) {
            hasWooCommerce = true;
            wooCommerceConfigData = data.config;
          }
        });
        
        console.log('Integrações configuradas:', { hasShopify, hasWooCommerce });
        setShopifyConfigured(hasShopify);
        setWooCommerceConfigured(hasWooCommerce);
        if (wooCommerceConfigData) {
          setWooCommerceConfig(wooCommerceConfigData);
        }
      } catch (error) {
        console.error('Erro ao verificar configurações de integração:', error);
      }
    };
    
    checkIntegrationsConfig();
  }, [currentUser]);

  const handleShopifyClick = () => {
    if (!shopifyConfigured) {
      // Redirecionar para a página de configuração
      navigate('/dashboard/integrations/settings');
      return;
    }
    
    // Se estiver configurado, continuar com a importação
    handleImport(importSources.find(s => s.id === 'shopify')!);
  };

  const handleWooCommerceClick = () => {
    if (!wooCommerceConfigured) {
      // Redirecionar para a página de configuração
      navigate('/dashboard/integrations/settings');
      return;
    }
    
    // Se estiver configurado, abrir a modal de importação
    setShowWooCommerceModal(true);
  };

  const handleManualClick = () => {
    // Redirecionar para a página de criação de produtos
    navigate('/dashboard/products/create');
  };

  const handleFeedXmlClick = () => {
    setShowFeedXmlModal(true);
  };

  const handleFeedXmlImport = async (products: any[]) => {
    if (!currentUser?.companyId) {
      toast({
        variant: "destructive",
        title: "Empresa não identificada",
        description: "É necessário estar autenticado com uma empresa para importar produtos."
      });
      return;
    }

    try {
      setLoading(true);
      
      console.log('Enviando requisição para /api/feed-xml/import-products:', {
        products: products.length,
        companyId: currentUser.companyId
      });
      
      // Chamar o endpoint para importar os produtos
      const response = await fetch('/api/feed-xml/import-products', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          products,
          companyId: currentUser.companyId
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(e => ({
          error: `Erro HTTP ${response.status}: ${response.statusText}`
        }));
        throw new Error(errorData.error || 'Erro ao importar produtos');
      }

      const result = await response.json();
      console.log('Resultado da importação:', result);
      
      toast({
        title: "Produtos importados com sucesso",
        description: `${result.count} produtos foram importados e estão aguardando aprovação.`
      });
      
      // Fechar o modal
      setShowFeedXmlModal(false);
    } catch (error) {
      console.error('Erro ao importar produtos do feed XML:', error);
      toast({
        variant: "destructive",
        title: "Erro ao importar produtos",
        description: error instanceof Error ? error.message : "Não foi possível importar os produtos do feed XML."
      });
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async (source: ImportSource) => {
    if (!source.enabled) {
      toast({
        variant: "destructive",
        title: "Esta fonte de importação ainda não está disponível",
        description: "Esta fonte de importação ainda não está disponível"
      });
      return;
    }

    if (!currentUser?.companyId) {
      toast({
        variant: "destructive",
        title: "Empresa não identificada",
        description: "Empresa não identificada"
      });
      return;
    }

    setLoading(true);
    try {
      switch (source.id) {
        case 'shopify':
          const handleImportShopify = async () => {
            try {
              setLoading(true);
              
              // Verificar se já existe configuração do Shopify
              const config = await shopifyService.getShopifyConfig(currentUser.companyId);
              
              if (!config) {
                // Se não há configuração, redirecionar para autorização
                const authUrl = await shopifyService.generateAuthUrl(currentUser.companyId);
                window.location.href = authUrl;
                return;
              }

              // Se já tem configuração, importar produtos diretamente
              const fetchedProducts = await shopifyService.getShopifyProducts(config);
              
              // Filtrar produtos já importados
              // Verificar manualmente se o produto já existe no Firestore
              const pendingProductsRef = collection(db, 'pendingProducts');
              const pendingQuery = query(pendingProductsRef, where('companyId', '==', currentUser.companyId));
              const pendingSnapshot = await getDocs(pendingQuery);
              
              const productsRef = collection(db, 'products');
              const productsQuery = query(productsRef, where('companyId', '==', currentUser.companyId));
              const productsSnapshot = await getDocs(productsQuery);
              
              // Criar um conjunto de IDs de produtos já importados
              const importedProductIds = new Set();
              
              pendingSnapshot.forEach(doc => {
                const data = doc.data();
                if (data.shopify && data.shopify.shopify_id) {
                  importedProductIds.add(data.shopify.shopify_id.toString());
                }
              });
              
              productsSnapshot.forEach(doc => {
                const data = doc.data();
                if (data.shopify && data.shopify.shopify_id) {
                  importedProductIds.add(data.shopify.shopify_id.toString());
                }
              });
              
              console.log('IDs de produtos já importados:', Array.from(importedProductIds));
              console.log('IDs de produtos do Shopify:', fetchedProducts.map(p => p.id.toString()));
              
              // Filtrar produtos que não estão no conjunto de IDs importados
              const newProducts = fetchedProducts.filter(product => {
                const productId = product.id.toString();
                return !importedProductIds.has(productId);
              });
              
              if (newProducts.length === 0) {
                toast({
                  variant: "destructive",
                  title: "Nenhum produto novo",
                  description: "Não há novos produtos para importar"
                });
                return;
              }
              
              const productsWithCompanyId = newProducts.map(product => ({
                ...product,
                companyId: currentUser.companyId
              }));
              
              setProducts(productsWithCompanyId);
              setShowProductsModal(true);
            } catch (error) {
              console.error('Erro ao importar:', error);
              toast({
                variant: "destructive",
                title: "Erro ao iniciar importação",
                description: "Erro ao iniciar importação"
              });
            } finally {
              setLoading(false);
            }
          };
          handleImportShopify();
          break;

        case 'woocommerce':
          handleWooCommerceClick();
          break;

        case 'manual':
          handleManualClick();
          break;

        case 'feed-xml':
          handleFeedXmlClick();
          break;

        default:
          toast({
            variant: "destructive",
            title: "Fonte de importação não implementada",
            description: "Fonte de importação não implementada"
          });
      }
    } catch (error) {
      console.error('Erro ao importar:', error);
      toast({
        variant: "destructive",
        title: "Erro ao iniciar importação",
        description: "Erro ao iniciar importação"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleImportSelected = async () => {
    try {
      setLoading(true);
      if (!currentUser?.companyId) {
        throw new Error('CompanyId não encontrado');
      }
      
      const productsToImport = products.filter(p => selectedProducts.includes(p.id));
      await shopifyService.importProducts(productsToImport, currentUser.companyId);
      
      toast({
        title: "Produtos importados com sucesso",
        description: "Os produtos selecionados foram importados e estão aguardando aprovação."
      });
      
      // navigate("/dashboard/products/pending");
      setProducts(products.map(product => ({ ...product, alreadyImported: selectedProducts.includes(product.id) })));
      setSelectedProducts([]);
    } catch (error) {
      console.error('Erro ao importar produtos:', error);
      toast({
        variant: "destructive",
        title: "Erro ao importar produtos",
        description: error instanceof Error ? error.message : "Não foi possível importar os produtos selecionados."
      });
    } finally {
      setLoading(false);
      setShowProductsModal(false);
    }
  };

  const toggleProductSelection = (productId: string) => {
    setSelectedProducts(prev => 
      prev.includes(productId) 
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  const selectAllProducts = () => {
    setSelectedProducts(products
      .filter(p => !p.alreadyImported)
      .map(p => p.id));
  };

  const deselectAllProducts = () => {
    setSelectedProducts([]);
  };

  const formatPrice = (price: string | undefined, currency: string = 'EUR') => {
    if (!price) return 'Preço não disponível';
    
    const value = parseFloat(price);
    const formatter = new Intl.NumberFormat('pt-PT', {
      style: 'currency',
      currency: currency
    });
    
    return formatter.format(value);
  };

  if (!currentUser?.role) {
    return (
      <div className="p-4">
        <p className="text-red-600">Acesso não autorizado</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      <h1 className="text-2xl font-bold mb-6">Importar Produtos</h1>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {importSources.map((source) => (
          <Card key={source.id} className="overflow-hidden">
            <CardHeader>
              <CardTitle>{source.name}</CardTitle>
              <CardDescription>
                {source.description}
              </CardDescription>
            </CardHeader>
            <CardContent className="flex justify-end">
              <Button
                onClick={() => source.id === 'woocommerce' ? handleWooCommerceClick() : source.id === 'shopify' ? handleShopifyClick() : source.id === 'manual' ? handleManualClick() : source.id === 'feed-xml' ? handleFeedXmlClick() : handleImport(source)}
                disabled={loading || (!source.enabled && source.id !== 'woocommerce' && source.id !== 'shopify' && source.id !== 'manual' && source.id !== 'feed-xml')}
                className={`w-full ${
                  source.enabled || (source.id === 'woocommerce' && !wooCommerceConfigured) || (source.id === 'shopify' && !shopifyConfigured) || (source.id === 'manual') || (source.id === 'feed-xml')
                    ? 'bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2'
                    : 'bg-gray-300 cursor-not-allowed'
                }`}
              >
                {loading
                  ? 'A importar...'
                  : source.id === 'woocommerce' && !wooCommerceConfigured
                    ? 'CONFIGURAR'
                    : source.id === 'shopify' && !shopifyConfigured
                      ? 'CONFIGURAR'
                      : source.id === 'manual'
                        ? 'Criar'
                        : source.id === 'feed-xml'
                          ? 'Importar'
                          : 'Importar'}
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={showProductsModal} onOpenChange={setShowProductsModal}>
        <DialogContent className="w-full max-w-4xl max-h-[80vh] overflow-y-auto bg-white">
          <DialogHeader>
            <DialogTitle className="text-xl">Selecionar Produtos para Importar</DialogTitle>
            <DialogDescription>
              Selecione os produtos que deseja importar para a plataforma.
            </DialogDescription>
          </DialogHeader>

          <div className="flex gap-2 mb-4">
            <Button variant="outline" onClick={selectAllProducts}>
              Selecionar Todos
            </Button>
            <Button variant="outline" onClick={deselectAllProducts}>
              Limpar Seleção
            </Button>
          </div>

          <div className="grid grid-cols-1 gap-4 mt-4">
            {products.filter(product => !product.alreadyImported).map((product) => (
              <div key={product.id} className={`flex items-start gap-4 p-4 border rounded-lg ${product.alreadyImported ? 'bg-gray-100' : 'hover:bg-gray-50'}`}>
                <Checkbox
                  id={`product-${product.id}`}
                  checked={selectedProducts.includes(product.id)}
                  onCheckedChange={() => toggleProductSelection(product.id)}
                  disabled={product.alreadyImported}
                />
                <div className="flex-1">
                  <div className="flex gap-4">
                    {product.images?.[0] && (
                      <img 
                        src={product.images[0].src} 
                        alt={product.title}
                        className="w-20 h-20 object-cover rounded-lg border"
                      />
                    )}
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium text-lg">{product.title}</h3>
                        {product.alreadyImported && (
                          <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">Já importado</span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500">
                        {product.variants?.[0]?.price 
                          ? formatPrice(product.variants[0].price, product.currency)
                          : 'Preço não disponível'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="flex justify-end gap-4 mt-6 pt-4 border-t">
            <Button variant="outline" onClick={() => setShowProductsModal(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleImportSelected}
              disabled={selectedProducts.length === 0 || loading}
              className="bg-primary text-white hover:bg-primary/90"
            >
              {loading ? "Importando..." : `Importar ${selectedProducts.length} produtos`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de importação de Feed XML */}
      <FeedXmlImportModal 
        open={showFeedXmlModal} 
        onClose={() => setShowFeedXmlModal(false)} 
        onImport={handleFeedXmlImport} 
      />

      {/* Modal de importação do WooCommerce */}
      <WooCommerceProductImport
        isOpen={showWooCommerceModal}
        onClose={() => setShowWooCommerceModal(false)}
        onSuccess={() => {
          setShowWooCommerceModal(false);
          toast({
            title: "Produtos importados com sucesso",
            description: "Os produtos foram importados e estão aguardando aprovação."
          });
        }}
      />
    </div>
  );
};
